import React, { Suspense, lazy, useState, useEffect, useCallback } from 'react';
import { useIsAuthenticated } from '@azure/msal-react';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { useNavigate } from 'react-router-dom';

// Ícones atualizados
import { 
  Search, FileText, Image as ImageIcon, Lock, Merge, Split, Minimize2, 
  Key, FileSignature, Film, Receipt, UserPlus, ClipboardList, X, ArrowLeft, FileCheck,
  Menu, ChevronRight, ChevronDown, Code, Braces, Table2, Sheet, Database, GitCompare,
} from 'lucide-react'; 

import Login from './components/Login';
import ScrollToTop from './components/ScrollToTop';
import {
  countUnreadRequisicoes,
  isRequisicaoUnread,
  markRequisicaoSeen,
  REQUISICOES_SEEN_EVENT,
} from './utils/requisicaoNotifications';
import {
  countUnreadPendencias,
  markAllPendenciasSeen,
  PENDENCIAS_SEEN_EVENT,
  resolveValidatorRole,
  isValidatorDepartamento,
  type ValidatorRole,
} from './utils/validatorNotifications';
import { hasRegisteredProfile } from './utils/toolPermissions';
import { rotaPendencias } from './utils/pendenciasRoutes';
import { apiFetch } from './utils/apiFetch';
import { showToast } from './notifications';

const FileConverter = lazy(() => import('./components/FileConverter'));
const PasswordBox = lazy(() => import('./components/Password'));
const SignatureBox = lazy(() => import('./components/signature'));
const DownloadVideo = lazy(() => import('./components/DownloadVideo'));
const ComparadorTiss = lazy(() => import('./features/comparador-tiss'));

const theme = createTheme({
    palette: {
        mode: 'light',
        primary: { main: '#2563eb', light: '#3b82f6', dark: '#1d4ed8' },
        secondary: { main: '#dc2626', light: '#ef4444', dark: '#b91c1c' },
        background: { default: '#f8fafc', paper: '#ffffff' },
    },
    typography: { fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif' },
    components: {
        MuiButton: {
            styleOverrides: {
                root: { borderRadius: 12, textTransform: 'none', fontWeight: 600, padding: '12px 24px' },
            },
        },
    },
});

interface Tool {
  id: string;               
  conversionId: string;     
  name: string;
  description: string;
  icon: any;
  category: string;
  tags: string[];
  color: string;            
  themeColor: string;       
  acceptedTypes: string[];
  multiple: boolean;
  button: string;
}

interface AvailableCard {
  key: string;
  name: string;
}

const CATEGORIES = ['Todos', 'Converter para PDF', 'Converter do PDF', 'Planilhas', 'Gerenciar PDFs', 'Utilitários & Segurança', 'Multimídia'];

const TOOLS_DATA: Tool[] = [
  { id: '1', conversionId: 'docx-to-pdf', name: 'Word para PDF', description: 'Converta documentos Word para PDF', icon: FileText, category: 'Converter para PDF', tags: ['.docx', '.pdf'], color: 'from-blue-500 to-blue-600', themeColor: '#3b82f6', acceptedTypes: ['.doc', '.docx'], multiple: false, button: 'Converter para PDF' },
  { id: '2', conversionId: 'jpg-to-pdf', name: 'JPG para PDF', description: 'Transforme imagens em PDF', icon: ImageIcon, category: 'Converter para PDF', tags: ['.jpg', '.pdf'], color: 'from-green-500 to-green-600', themeColor: '#f59e0b', acceptedTypes: ['.jpg', '.jpeg', '.png'], multiple: true, button: 'Converter para PDF' },
  { id: '3', conversionId: 'excel-to-pdf', name: 'Excel para PDF', description: 'Converta planilhas para PDF', icon: FileText, category: 'Converter para PDF', tags: ['.xlsx', '.pdf'], color: 'from-emerald-500 to-emerald-600', themeColor: '#06b6d4', acceptedTypes: ['.xlsx', '.xls'], multiple: false, button: 'Converter para PDF' },
  { id: '19', conversionId: 'csv-to-pdf', name: 'CSV para PDF', description: 'Converta planilhas CSV em documento PDF', icon: Table2, category: 'Converter para PDF', tags: ['.csv', '.pdf'], color: 'from-violet-500 to-violet-600', themeColor: '#8b5cf6', acceptedTypes: ['.csv'], multiple: false, button: 'Converter para PDF' },
  { id: '4', conversionId: 'pdf-to-docx', name: 'PDF para Word', description: 'Extraia conteúdo do PDF', icon: FileText, category: 'Converter do PDF', tags: ['.pdf', '.docx'], color: 'from-blue-500 to-blue-600', themeColor: '#0f766e', acceptedTypes: ['.pdf'], multiple: false, button: 'Converter para Word' },
  { id: '5', conversionId: 'pdf-to-jpg', name: 'PDF para JPG', description: 'Converta páginas em imagens', icon: ImageIcon, category: 'Converter do PDF', tags: ['.pdf', '.jpg'], color: 'from-cyan-500 to-cyan-600', themeColor: '#10b981', acceptedTypes: ['.pdf'], multiple: false, button: 'Converter para JPG' },
  { id: '6', conversionId: 'pdf-to-excel', name: 'PDF para Excel', description: 'Extraia tabelas do PDF', icon: FileText, category: 'Converter do PDF', tags: ['.pdf', '.xlsx'], color: 'from-green-500 to-green-600', themeColor: '#059669', acceptedTypes: ['.pdf'], multiple: false, button: 'Converter para Excel' },
  { id: '20', conversionId: 'pdf-to-base64', name: 'PDF para Base64', description: 'Codifique PDF em Base64 (arquivo JSON)', icon: Code, category: 'Converter do PDF', tags: ['.pdf', '.json'], color: 'from-purple-500 to-purple-600', themeColor: '#7c3aed', acceptedTypes: ['.pdf'], multiple: false, button: 'Converter para Base64' },
  { id: '21', conversionId: 'base64-to-pdf', name: 'Base64 para PDF', description: 'Decodifique Base64 e gere um PDF', icon: Braces, category: 'Converter do PDF', tags: ['.json', '.txt', '.pdf'], color: 'from-red-500 to-red-600', themeColor: '#dc2626', acceptedTypes: ['.json', '.txt'], multiple: false, button: 'Converter para PDF' },
  { id: '22', conversionId: 'csv-to-xlsx', name: 'CSV para Excel', description: 'Converta planilhas CSV para Excel (XLSX)', icon: Table2, category: 'Planilhas', tags: ['.csv', '.xlsx'], color: 'from-green-500 to-green-600', themeColor: '#16a34a', acceptedTypes: ['.csv'], multiple: false, button: 'Converter para Excel' },
  { id: '23', conversionId: 'excel-to-csv', name: 'Excel para CSV', description: 'Exporte planilhas Excel para formato CSV', icon: Sheet, category: 'Planilhas', tags: ['.xlsx', '.csv'], color: 'from-teal-500 to-teal-600', themeColor: '#0d9488', acceptedTypes: ['.xlsx', '.xls'], multiple: false, button: 'Converter para CSV' },
  { id: '24', conversionId: 'spreadsheet-to-dbf', name: 'Excel/CSV para DBF', description: 'Converta planilhas XLSX ou CSV para formato DBF (dBase)', icon: Database, category: 'Planilhas', tags: ['.xlsx', '.csv', '.dbf'], color: 'from-amber-500 to-amber-600', themeColor: '#d97706', acceptedTypes: ['.csv', '.xlsx', '.xls'], multiple: false, button: 'Converter para DBF' },
  { id: '7', conversionId: 'merge-pdfs', name: 'Unir PDF', description: 'Combine vários PDFs em um', icon: Merge, category: 'Gerenciar PDFs', tags: ['.pdf'], color: 'from-purple-500 to-purple-600', themeColor: '#ef4444', acceptedTypes: ['.pdf'], multiple: true, button: 'Unir PDFs' },
  { id: '8', conversionId: 'split-pdf', name: 'Dividir PDF', description: 'Separe páginas do PDF', icon: Split, category: 'Gerenciar PDFs', tags: ['.pdf'], color: 'from-pink-500 to-pink-600', themeColor: '#ec4899', acceptedTypes: ['.pdf'], multiple: false, button: 'Dividir PDF' },
  { id: '9', conversionId: 'compress-arquivo', name: 'Comprimir arquivo', description: 'Reduza o tamanho de arquivos', icon: Minimize2, category: 'Gerenciar PDFs', tags: ['.pdf', '.mp3', '.video'], color: 'from-orange-500 to-orange-600', themeColor: '#84cc16', acceptedTypes: ['.pdf', '.mp3', '.mp4', '.mov'], multiple: false, button: 'Comprimir PDF' },
  { id: '10', conversionId: 'proteger-pdf', name: 'Proteger PDF', description: 'Adicione senha ao PDF', icon: Lock, category: 'Utilitários & Segurança', tags: ['.pdf'], color: 'from-red-500 to-red-600', themeColor: '#ef4444', acceptedTypes: ['.pdf'], multiple: false, button: 'Proteger PDF' },
  { id: '11', conversionId: 'password-generator', name: 'Gerador de Senhas', description: 'Crie senhas seguras', icon: Key, category: 'Utilitários & Segurança', tags: [], color: 'from-amber-500 to-amber-600', themeColor: '#360d01', acceptedTypes: [], multiple: false, button: 'Gerar Senha' },
  { id: '12', conversionId: 'signature-generator', name: 'Assinatura de Email', description: 'Crie a assinatura do seu email', icon: FileSignature, category: 'Utilitários & Segurança', tags: [], color: 'from-indigo-500 to-indigo-600', themeColor: '#8a7065', acceptedTypes: [], multiple: false, button: 'Gerar Assinatura' },
  { id: '13', conversionId: 'mp4-to-gif', name: 'MP4 para GIF', description: 'Converta vídeos em GIF', icon: Film, category: 'Multimídia', tags: ['.mp4', '.gif'], color: 'from-violet-500 to-violet-600', themeColor: '#e91e63', acceptedTypes: ['.mp4', '.mov', '.avi', '.mkv'], multiple: false, button: 'Converter para GIF' },
  { id: '16', conversionId: 'cadastro-prestador', name: 'Cadastro de Conta Contábil', description: 'Cadastre despesas e prestadores automaticamente', icon: Receipt, category: 'Utilitários & Segurança', tags: [], color: 'from-teal-500 to-teal-600', themeColor: '#0d9488', acceptedTypes: [], multiple: false, button: 'Emitir' },
  { id: '17', conversionId: 'cadastro-fornecedor', name: 'Cadastro de Fornecedor', description: 'Submeta novos fornecedores', icon: UserPlus, category: 'Utilitários & Segurança', tags: [], color: 'from-sky-500 to-sky-600', themeColor: '#0284c7', acceptedTypes: [], multiple: false, button: 'Cadastrar' },
  { id: '18', conversionId: 'download-video', name: 'Download de Vídeo', description: 'Baixe vídeos do YouTube em MP4 ou somente o áudio em MP3', icon: Film, category: 'Multimídia', tags: ['YouTube', 'MP4', 'MP3'], color: 'from-red-500 to-red-600', themeColor: '#ef4444', acceptedTypes: [], multiple: false, button: 'Baixar do YouTube' },
  { id: '25', conversionId: 'comparador-tiss', name: 'Comparador de Arquivo Tiss', description: 'Compare arquivos TISS 4.02 lado a lado com visão estruturada e diff', icon: GitCompare, category: 'Utilitários & Segurança', tags: ['.xml', '.json', 'TISS'], color: 'from-[#660055] to-[#993399]', themeColor: '#660055', acceptedTypes: ['.xml', '.json', '.sql', '.csv', '.txt'], multiple: true, button: 'Comparar' }
];
const TOOLS_BY_KEY = new Map(TOOLS_DATA.map((tool) => [tool.conversionId, tool]));
const CARDS_CACHE_PREFIX = 'home_cards:';

function cardsCacheKey(email: string) {
  return `${CARDS_CACHE_PREFIX}${email.trim().toLowerCase()}`;
}

function readCachedCardKeys(email: string): string[] | null {
  if (!email) return null;
  try {
    const raw = sessionStorage.getItem(cardsCacheKey(email));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    return parsed.filter((key): key is string => typeof key === 'string');
  } catch {
    return null;
  }
}

function writeCachedCardKeys(email: string, keys: string[]) {
  if (!email) return;
  sessionStorage.setItem(cardsCacheKey(email), JSON.stringify(keys));
}

function toolsFromKeys(keys: string[]): Tool[] {
  return keys
    .map((key) => TOOLS_BY_KEY.get(key))
    .filter((tool): tool is Tool => Boolean(tool));
}

function ToolPanelFallback() {
  return (
    <div className="flex justify-center py-16" role="status" aria-label="Carregando ferramenta">
      <div className="h-10 w-10 rounded-full border-2 border-[#993399] border-t-transparent animate-spin" />
    </div>
  );
}

export default function Home() {
  const isAuthenticated = useIsAuthenticated();
  
  const [selectedTool, setSelectedTool] = useState<Tool | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Todos');
  const [availableTools, setAvailableTools] = useState<Tool[]>(() => {
    const keys = readCachedCardKeys(localStorage.getItem('user_email') || '');
    return keys ? toolsFromKeys(keys) : [];
  });
  const [cardsLoaded, setCardsLoaded] = useState(
    () => readCachedCardKeys(localStorage.getItem('user_email') || '') !== null,
  );
  
  const navigate = useNavigate();
  
  // Estados para o Menu Lateral (Sidebar)
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showRequisitionsList, setShowRequisitionsList] = useState(false);
  
  const [todasRequisicoes, setTodasRequisicoes] = useState<any[]>([]);
  const [requisicoesCarregadas, setRequisicoesCarregadas] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [pendingUnreadCount, setPendingUnreadCount] = useState(0);
  const [userDepartamento, setUserDepartamento] = useState(
    () => localStorage.getItem('user_departamento') || '',
  );
  const emailLogado = localStorage.getItem('user_email') || '';
  const hasProfile = hasRegisteredProfile(userDepartamento);
  const isValidator = hasProfile && isValidatorDepartamento(userDepartamento);
  const destinoPendencias = rotaPendencias(userDepartamento);
  const hasRequisicoes = todasRequisicoes.length > 0;
  const showActionsMenu =
    Boolean(emailLogado) &&
    (Boolean(destinoPendencias) || (hasProfile && requisicoesCarregadas && hasRequisicoes));

  const menuBadgeCount =
    (hasRequisicoes ? unreadCount : 0) + (isValidator ? pendingUnreadCount : 0);

  const fetchAvailableCards = useCallback(async () => {
    try {
      const response = await apiFetch('/api/cards');
      if (!response.ok) {
        throw new Error(`Falha ao carregar cards: ${response.status}`);
      }
      const cards: AvailableCard[] = await response.json();
      const keys = cards.map((card) => card.key);
      writeCachedCardKeys(localStorage.getItem('user_email') || '', keys);
      setAvailableTools(toolsFromKeys(keys));
    } catch (error) {
      console.error('Erro ao carregar cards disponíveis', error);
      setAvailableTools((current) => current);
    } finally {
      setCardsLoaded(true);
    }
  }, []);

  const atualizarContagem = useCallback((lista: any[]) => {
    if (!emailLogado) {
      setUnreadCount(0);
      return;
    }
    setUnreadCount(countUnreadRequisicoes(lista, emailLogado));
  }, [emailLogado]);

  const fetchRequisicoes = useCallback(async () => {
    if (!emailLogado) {
      setTodasRequisicoes([]);
      setRequisicoesCarregadas(true);
      return;
    }

    try {
      const res = await apiFetch(`/api/cadastros/usuario/${encodeURIComponent(emailLogado)}`);
      if (res.ok) {
        const data = await res.json();
        setTodasRequisicoes(data);
        atualizarContagem(data);
      } else {
        console.error('Erro ao carregar requisições:', res.status);
      }
    } catch (err) {
      console.error("Erro ao carregar requisições");
    } finally {
      setRequisicoesCarregadas(true);
    }
  }, [atualizarContagem, emailLogado]);

  const syncDepartamento = useCallback(async (): Promise<ValidatorRole | null> => {
    const email = localStorage.getItem('user_email');
    if (!email) return null;
    try {
      const res = await apiFetch('/api/autorizacao', { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        if (data.cadastrado && data.autorizado && data.departamento) {
          localStorage.setItem('user_departamento', data.departamento);
          if (data.nome) localStorage.setItem('user_nome', data.nome);
          setUserDepartamento(data.departamento);
          return resolveValidatorRole(data.departamento);
        }
        localStorage.removeItem('user_departamento');
        setUserDepartamento('');
      }
    } catch {
      /* ignora */
    }
    return resolveValidatorRole(localStorage.getItem('user_departamento'));
  }, []);

  const fetchPendenciasValidador = useCallback(async (roleOverride?: ValidatorRole | null) => {
    const email = localStorage.getItem('user_email') || '';
    const role =
      roleOverride !== undefined
        ? roleOverride
        : resolveValidatorRole(localStorage.getItem('user_departamento'));
    if (roleOverride === undefined) {
      setUserDepartamento(localStorage.getItem('user_departamento') || '');
    }
    if (!email || !role) {
      setPendingUnreadCount(0);
      return;
    }

    try {
      const res = await apiFetch(
        role === 'compras' ? '/api/cadastros/pendentes' : '/api/cadastros/pendentes/contabil',
      );
      if (res.ok) {
        const data = await res.json();
        const items = data.map((item: { id: number; status: string }) => ({
          id: item.id,
          status: item.status,
        }));
        setPendingUnreadCount(countUnreadPendencias(items, email, role));
      }
    } catch (err) {
      console.error('Erro ao carregar pendências do validador');
    }
  }, []);

  const refreshNotificacoes = useCallback(async () => {
    const role = await syncDepartamento();
    fetchRequisicoes();
    if (role) {
      fetchPendenciasValidador(role);
    } else {
      setPendingUnreadCount(0);
    }
  }, [fetchPendenciasValidador, fetchRequisicoes, syncDepartamento]);

  useEffect(() => {
    fetchAvailableCards();
  }, [fetchAvailableCards]);

  useEffect(() => {
    refreshNotificacoes();

    const onRequisicoesSeen = () => {
      const email = localStorage.getItem('user_email') || '';
      setTodasRequisicoes((prev) => {
        setUnreadCount(countUnreadRequisicoes(prev, email));
        return prev;
      });
    };
    const onPendenciasSeen = () => fetchPendenciasValidador();

    window.addEventListener(REQUISICOES_SEEN_EVENT, onRequisicoesSeen);
    window.addEventListener(PENDENCIAS_SEEN_EVENT, onPendenciasSeen);
    window.addEventListener('focus', refreshNotificacoes);
    const interval = window.setInterval(refreshNotificacoes, 45000);
    return () => {
      window.removeEventListener(REQUISICOES_SEEN_EVENT, onRequisicoesSeen);
      window.removeEventListener(PENDENCIAS_SEEN_EVENT, onPendenciasSeen);
      window.removeEventListener('focus', refreshNotificacoes);
      window.clearInterval(interval);
    };
  }, [fetchPendenciasValidador, refreshNotificacoes]);

  useEffect(() => {
    if (cardsLoaded && selectedTool && !availableTools.some((tool) => tool.id === selectedTool.id)) {
      setSelectedTool(null);
    }
  }, [availableTools, cardsLoaded, selectedTool]);

  const previewRequisicoes = todasRequisicoes.slice(0, 4);

  const handleRequisicaoHover = (req: { id: number; status: string }) => {
    if (emailLogado) {
      markRequisicaoSeen(emailLogado, req.id, req.status);
    }
  };

  const handleRequisicaoClick = () => {
    navigate('/minhas-requisicoes');
  };

  if (!isAuthenticated) {
    return <Login />;
  }

  // Função auxiliar para pintar as etiquetas de status no preview
  const getStatusBadge = (status: string) => {
    switch(status) {
      case 'PENDENTE_COMPRAS': return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'EM_ANDAMENTO_COMPRAS': return 'bg-orange-50 text-orange-700 border-orange-200';
      case 'CORRECAO_COMPRAS': return 'bg-[#FFF0EB] text-[#FF6633] border-[#FF6633]/30';
      case 'REPROVADO': return 'bg-red-50 text-red-700 border-red-200';
      case 'PENDENTE_CONTABIL': return 'bg-purple-50 text-purple-700 border-purple-200';
      case 'CORRECAO_CONTABIL': return 'bg-purple-50 text-purple-800 border-purple-300';
      case 'APROVADO_INTEGRADO': return 'bg-green-50 text-green-700 border-green-200';
      default: return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const handlePendenciasClick = async () => {
    const emailUsuario = localStorage.getItem('user_email'); 

    if (!emailUsuario) {
      showToast({ type: 'warning', title: 'Sessão inválida', message: 'Por favor, faça login novamente no sistema.' });
      return;
    }

    try {
      const response = await apiFetch('/api/autorizacao', { method: 'POST' });

      if (response.ok) {
        const data = await response.json();
        const departamentoReal = data.departamento;

        if (!data.cadastrado || !departamentoReal) {
          showToast({ type: 'info', title: 'Tudo certo por aqui!', message: 'O seu perfil não possui validações documentais pendentes no momento.' });
          return;
        }

        localStorage.setItem('user_departamento', departamentoReal);
        setUserDepartamento(departamentoReal);

        const role = resolveValidatorRole(departamentoReal);
        if (role) {
          try {
            const pendRes = await apiFetch(
              role === 'compras' ? '/api/cadastros/pendentes' : '/api/cadastros/pendentes/contabil',
            );
            if (pendRes.ok) {
              const pendencias = await pendRes.json();
              markAllPendenciasSeen(
                emailUsuario,
                role,
                pendencias.map((item: { id: number; status: string }) => ({
                  id: item.id,
                  status: item.status,
                })),
              );
              setPendingUnreadCount(0);
            }
          } catch {
            /* segue navegação mesmo se falhar o mark */
          }
        }

        const destino = rotaPendencias(departamentoReal);
        if (!destino) {
          showToast({ type: 'info', title: 'Tudo certo por aqui!', message: 'O seu perfil não possui validações documentais pendentes no momento.' });
          return;
        }
        navigate(destino);
      } else if (response.status === 401) {
        showToast({ type: 'warning', title: 'Sessão inválida', message: 'Por favor, faça login novamente no sistema.' });
      } else {
        showToast({ type: 'error', title: 'Falha na verificação', message: 'Não foi possível verificar suas permissões. Tente novamente.' });
      }
    } catch (error) {
      console.error("Erro ao validar permissão no banco", error);
      showToast({ type: 'error', title: 'Erro de conexão', message: 'Erro de conexão com o banco de dados ao verificar as pendências.' });
    }
  };
  
  const handleToolClick = (tool: Tool) => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    if (tool.id === '17') {
      navigate('/cadastro');
      return; 
    }
    if (tool.id === '16') {
      navigate('/CadPrestador');
      return; 
    }
    setSelectedTool(tool);
  };

  const filteredTools = availableTools.filter(tool => {
    const matchesSearch = tool.name.toLowerCase().includes(searchTerm.toLowerCase()) || tool.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'Todos' || tool.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const favoriteTools = availableTools.slice(0, 4);

  if (selectedTool && selectedTool.conversionId === 'comparador-tiss') {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <div className="h-screen flex flex-col bg-gradient-to-br from-[#E6EAE1] to-[#E6E6F6] font-sans">
          <header className="shrink-0 bg-white/40 backdrop-blur-sm border-b border-gray-200">
            <div className="relative flex items-center px-4 py-2">
              <button onClick={() => setSelectedTool(null)} className="inline-flex items-center justify-center p-2 rounded-xl bg-white/60 hover:bg-white/80 text-[#660055] border border-[#993399] transition-all outline-none text-sm h-9 px-4 shadow-sm">
                <ArrowLeft className="w-4 h-4 mr-2" /> Voltar
              </button>
              <img
                src="/ilevepdf_logo.png"
                alt="ILevePDF Logo"
                className="pointer-events-none absolute left-1/2 top-1/2 h-9 w-auto -translate-x-1/2 -translate-y-1/2 object-contain mix-blend-multiply"
              />
            </div>
          </header>

          <main className="flex-1 min-h-0">
            <Suspense fallback={<ToolPanelFallback />}>
              <ComparadorTiss />
            </Suspense>
          </main>
        </div>
      </ThemeProvider>
    );
  }

  if (selectedTool) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <div className="min-h-screen bg-gradient-to-br from-[#E6EAE1] to-[#E6E6F6] font-sans">
          <header className="bg-white/40 backdrop-blur-sm border-b border-gray-200">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
              <div className="relative flex items-center justify-between">
                <div className="flex-1">
                  <button onClick={() => setSelectedTool(null)} className="inline-flex items-center justify-center p-2 rounded-xl bg-white/60 hover:bg-white/80 text-[#660055] border border-[#993399] transition-all outline-none text-sm h-9 px-4 shadow-sm">
                    <ArrowLeft className="w-4 h-4 mr-2" /> Voltar
                  </button>
                </div>
                <div className="flex-1 flex justify-center items-center">
                  <img src="/ilevepdf_logo.png" alt="ILevePDF Logo" className="h-16 w-auto object-contain mix-blend-multiply" />
                </div>
                <div className="flex-1"></div>
              </div>
            </div>
          </header>

          <main className="mx-auto px-4 py-12 max-w-4xl">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-[#660055] mb-2">{selectedTool.name}</h2>
              <p className="text-gray-600">{selectedTool.description}</p>
            </div>

            <Suspense fallback={<ToolPanelFallback />}>
              {selectedTool.conversionId === 'password-generator' ? (
                <PasswordBox />
              ) : selectedTool.conversionId === 'signature-generator' ? (
                <SignatureBox themeColor={selectedTool.themeColor} />
              ) : selectedTool.conversionId === 'download-video' ? (
                <DownloadVideo themeColor={selectedTool.themeColor} />
              ) : (
                <FileConverter
                  conversionType={selectedTool.conversionId as any}
                  option={{ ...selectedTool, color: selectedTool.themeColor } as any}
                />
              )}
            </Suspense>
          </main>
        </div>
      </ThemeProvider>
    );
  }

  const cardsPending = !cardsLoaded && availableTools.length === 0;

  return (
      <div className="min-h-screen bg-gradient-to-br from-[#E6EAE1] to-[#E6E6F6] font-sans">
        
        <header className="bg-white/40 backdrop-blur-sm border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
            <div className="relative flex items-center justify-between">
              
              <div className="flex-1"></div>
              
              <div className="flex-1 flex justify-center items-center">
                <img src="/ilevepdf_logo.png" alt="ILevePDF Logo" className="h-16 w-auto object-contain mix-blend-multiply" />
              </div>

              <div className="flex-1 flex justify-end gap-2">
                {showActionsMenu && (
                  <button 
                    onClick={() => { setIsMenuOpen(true); refreshNotificacoes(); }} 
                    className="relative inline-flex items-center justify-center bg-white/60 hover:bg-white/80 text-[#660055] border border-[#993399] rounded-lg h-10 w-10 transition-all shadow-sm outline-none"
                    aria-label="Menu de pendências e requisições"
                  >
                    <Menu className="w-5 h-5" />
                    {menuBadgeCount > 0 && (
                      <span className="absolute -top-1.5 -right-1.5 bg-orange-500 text-white text-[10px] rounded-full min-w-5 h-5 px-1 flex items-center justify-center font-bold shadow-sm">
                        {menuBadgeCount > 9 ? '9+' : menuBadgeCount}
                      </span>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </header>

        {showActionsMenu && isMenuOpen && (
          <div className="fixed inset-0 bg-black/40 z-50 transition-opacity backdrop-blur-sm" onClick={() => setIsMenuOpen(false)}>
            <div 
              className="absolute right-0 top-0 h-full w-full max-w-sm bg-white shadow-2xl overflow-y-auto animate-in slide-in-from-right-full duration-300 flex flex-col" 
              onClick={e => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between z-10">
                <h2 className="text-xl font-bold text-[#660055]">Menu de Ações</h2>
                <button 
                  onClick={() => setIsMenuOpen(false)} 
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors outline-none"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              <div className="p-6 flex flex-col gap-4">

                {destinoPendencias && (
                  <button 
                    onClick={() => {
                      setIsMenuOpen(false);
                      handlePendenciasClick();
                    }}
                    className="relative w-full flex items-center justify-between p-4 rounded-xl border border-[#E6E6F6] bg-white hover:bg-gray-50 hover:border-[#993399]/40 transition-all group text-left shadow-sm"
                  >
                    <div className="flex items-center gap-4">
                      <div className="p-2.5 bg-[#f3e6f0] text-[#660055] rounded-lg group-hover:scale-110 transition-transform">
                        <FileCheck className="w-5 h-5" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-800 text-sm">Pendências</h3>
                        <p className="text-[11px] text-gray-500 mt-0.5">Validações e aprovações</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {pendingUnreadCount > 0 && (
                        <span className="bg-[#660055] text-white text-[10px] rounded-full min-w-5 h-5 px-1 flex items-center justify-center font-bold">
                          {pendingUnreadCount > 9 ? '9+' : pendingUnreadCount}
                        </span>
                      )}
                      <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-[#660055] transition-colors" />
                    </div>
                  </button>
                )}

                {hasRequisicoes && (
                <div className="border border-[#E6E6F6] rounded-xl overflow-hidden bg-white shadow-sm transition-all">
                  <button 
                    onClick={() => setShowRequisitionsList(!showRequisitionsList)}
                    className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors text-left"
                  >
                    <div className="flex items-center gap-4">
                      <div className="p-2.5 bg-[#f3e6f0] text-[#660055] rounded-lg">
                        <ClipboardList className="w-5 h-5" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-800 text-sm">Minhas Requisições</h3>
                        <p className="text-[11px] text-gray-500 mt-0.5">Acompanhe seus processos</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {unreadCount > 0 && (
                        <span className="bg-orange-500 text-white text-[10px] rounded-full min-w-5 h-5 px-1 flex items-center justify-center font-bold">
                          {unreadCount > 9 ? '9+' : unreadCount}
                        </span>
                      )}
                      <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform duration-300 ${showRequisitionsList ? 'rotate-180' : ''}`} />
                    </div>
                  </button>

                  {/* DADOS DINÂMICOS DO BANCO AQUI */}
                  {showRequisitionsList && (
                    <div className="p-4 bg-[#FAFBFC] border-t border-[#E6E6F6]">
                      {previewRequisicoes.length === 0 ? (
                        <div className="text-center py-6">
                          <ClipboardList className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                          <p className="text-sm text-gray-500">Nenhuma requisição ativa.</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {previewRequisicoes.map((req) => {
                            const unread = emailLogado && isRequisicaoUnread(req, emailLogado);
                            return (
                            <div 
                              key={req.id} 
                              onMouseEnter={() => handleRequisicaoHover(req)}
                              onClick={handleRequisicaoClick}
                              className={`bg-white rounded-xl p-3 border shadow-sm hover:shadow-md transition-shadow cursor-pointer ${
                                unread ? 'border-orange-300 ring-1 ring-orange-200' : 'border-[#E6E6F6]'
                              }`}
                            >
                              <div className="flex flex-col gap-1.5">
                                <div className="flex items-center gap-2">
                                  {unread && (
                                    <span className="w-2 h-2 rounded-full bg-orange-500 flex-shrink-0" title="Atualização não lida" />
                                  )}
                                  <h3 className="text-gray-800 font-bold text-xs truncate flex-1" title={req.razao_social}>
                                    {req.razao_social}
                                  </h3>
                                  {req.tipo_cadastro && (
                                    <span className="text-[9px] text-gray-400 uppercase">{req.tipo_cadastro}</span>
                                  )}
                                </div>
                                <div className="flex items-center justify-between">
                                  <span className={`inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider ${getStatusBadge(req.status)}`}>
                                    {req.status.replace(/_/g, ' ')}
                                  </span>
                                </div>
                              </div>
                            </div>
                          )})}
                          
                          <button 
                            onClick={() => navigate('/minhas-requisicoes')}
                            className="w-full mt-3 py-2 text-xs font-bold text-[#660055] hover:bg-[#f3e6f0] rounded-lg transition-colors border border-transparent hover:border-[#e8ccdf]"
                          >
                            Ver histórico completo →
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                )}

              </div>
            </div>
          </div>
        )}

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          
          <div className="mb-12">
            <div className="relative max-w-3xl mx-auto">
              <Search className="absolute left-6 top-1/2 -translate-y-1/2 text-gray-400 w-6 h-6" />
              <input type="text" placeholder="O que você precisa fazer hoje?" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-16 pr-6 py-5 rounded-2xl bg-white shadow-xl text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#993399] transition-all" />
            </div>
          </div>

          <div className="mb-10 overflow-x-auto pb-2 scrollbar-hide">
            <div className="flex gap-3 justify-center min-w-max px-4">
              {CATEGORIES.map(category => (
                <button key={category} onClick={() => setSelectedCategory(category)} className={`px-6 py-3 rounded-xl transition-all whitespace-nowrap border outline-none font-medium ${selectedCategory === category ? 'bg-white shadow-lg text-[#660055] border-[#993399]' : 'bg-white/60 hover:bg-white/80 text-gray-600 border-transparent'}`}>
                  {category}
                </button>
              ))}
            </div>
          </div>

          {searchTerm === '' && selectedCategory === 'Todos' && (cardsPending || favoriteTools.length > 0) && (
            <section className="mb-12">
              <h2 className="text-2xl mb-6 text-center font-semibold text-[#660055]">Ferramentas Mais Usadas</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {cardsPending
                  ? Array.from({ length: 4 }, (_, index) => <ToolCardSkeleton key={index} />)
                  : favoriteTools.map(tool => (
                    <ToolCard key={tool.id} tool={tool} onClick={() => handleToolClick(tool)} />
                  ))}
              </div>
            </section>
          )}

          <section>
            {searchTerm === '' && selectedCategory === 'Todos' && (
              <h2 className="text-2xl mb-6 text-center font-semibold text-[#660055]">Todas as Ferramentas</h2>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {cardsPending
                ? Array.from({ length: 8 }, (_, index) => <ToolCardSkeleton key={index} />)
                : filteredTools.map(tool => (
                  <ToolCard key={tool.id} tool={tool} onClick={() => handleToolClick(tool)} />
                ))}
            </div>
            {cardsLoaded && filteredTools.length === 0 && (
              <div className="text-center py-16 bg-white/40 rounded-3xl border border-dashed border-gray-300">
                <p className="text-gray-600 text-xl font-medium">Nenhuma ferramenta encontrada</p>
                <p className="text-gray-500 mt-2">Tente buscar por outro termo ou limpe os filtros.</p>
              </div>
            )}
          </section>
        </main>

        <footer className="mt-20 pb-8 border-t border-gray-200/50 pt-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="text-center text-gray-500 text-sm">© 2024 ILevePDF - Todas as ferramentas que você precisa em um só lugar.</p>
            <p className="mt-1 text-center text-gray-500 text-sm">Desenvolvido pelo time de Sistemas - Leve Saúde.</p>
          </div>
        </footer>

        <ScrollToTop />
      </div>
  );
}

function ToolCardSkeleton() {
  return (
    <div className="bg-white rounded-2xl p-6 border border-[#993399]/30 animate-pulse" aria-hidden="true">
      <div className="w-14 h-14 rounded-xl bg-gray-200 mb-4" />
      <div className="h-4 bg-gray-200 rounded w-2/3 mb-3" />
      <div className="h-3 bg-gray-100 rounded w-full mb-2" />
      <div className="h-3 bg-gray-100 rounded w-4/5" />
    </div>
  );
}

function ToolCard({ tool, onClick }: { tool: Tool; onClick: () => void }) {
  const Icon = tool.icon;
  return (
    <div onClick={onClick} className="group bg-white rounded-2xl p-6 cursor-pointer transition-all hover:shadow-2xl hover:-translate-y-2 hover:scale-105 duration-300 border border-[#993399]">
      <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${tool.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
        <Icon className="w-7 h-7 text-white" />
      </div>
      <h3 className="text-gray-800 mb-2 font-medium">{tool.name}</h3>
      <p className="text-gray-500 text-sm mb-4 leading-relaxed">{tool.description}</p>
      
      {tool.tags.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          {tool.tags.map((tag, index) => (
            <span key={index} className="inline-flex items-center justify-center rounded-md border border-transparent px-2.5 py-0.5 text-xs font-semibold bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors">
              {tag}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}